import type { Survey } from 'types/settings';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { ButtonClose, Logo } from '@leafwell/components';
import { usePostHog } from 'posthog-js/react';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useBrazeContext } from 'contexts/braze-context';
import { useMatchMedia } from 'hooks/useMatchMedia';

import { getRedirectUrl } from 'lib/data/survey-redirect-rules';
import WysiwygContent from './wysiwyg-content';

const Survey: React.FC<Survey> = ({ surveyId, steps }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rawAnswers, setRawAnswers] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [email, setEmail] = useState('');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [headers, setHeaders] = useState<{
    thankYouHeader?: string;
    thankYouDescription?: string;
    buttonLabel?: string;
    imageUrl?: string;
    imageClassName?: string;
  }>({
    thankYouHeader: '',
    thankYouDescription: '',
    buttonLabel: '',
    imageUrl: '',
    imageClassName: '',
  });
  const [show, setShow] = useState(true);
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [isLargeScreen] = useMatchMedia(['(min-width: 1024px)'], [true]);

  const step = steps[stepIndex];

  const posthog = usePostHog();
  const { trackEvent } = useEventsTracker();
  const { saveUser: saveBrazeUser, requestImmediateDataFlush } =
    useBrazeContext();

  const redirectNow = (url: string) => {
    window.open(url, '_blank');
    setShow(false);
  };

  useEffect(() => {
    trackEvent({
      event: 'survey_show',
      survey_id: surveyId,
      origin: window.location.href,
    });
  }, []);

  useEffect(() => {
    if (step.stepType === 'submit' && !hasSubmitted) {
      handleSubmit();
    }
  }, [step.stepType]);

  const handleSubmit = () => {
    if (hasSubmitted) return; // 🔐 prevent double-execution
    setHasSubmitted(true);

    const redirectTo = getRedirectUrl(surveyId, rawAnswers);
    setRedirectUrl(redirectTo.buttonUrl);

    if (email) {
      posthog.alias(email);
      saveBrazeUser({ email });
    }

    trackEvent({
      event: 'survey_submit',
      survey_id: surveyId,
      answers: answers,
      submit_button_url:
        step.submitBehavior === 'dynamic'
          ? redirectTo
          : step.submitBehavior === 'link'
          ? step.buttonUrl
          : 'close_form',
      email,
      origin: window.location.href,
    });

    requestImmediateDataFlush();

    localStorage.setItem(`survey_submitted_${surveyId}`, 'true');

    if (step.submitBehavior === 'dynamic' && !redirectTimeoutRef.current) {
      redirectTimeoutRef.current = setTimeout(() => {
        window.open(redirectTo.buttonUrl, '_blank');
        setShow(false);
      }, 4000);
    }

    if (step.submitBehavior === 'close') {
      setShow(false);
    }
  };

  const handleNext = () => {
    const nextStep = steps[stepIndex + 1];
    if (nextStep.stepType === 'submit') {
      const {
        thankYouHeader,
        thankYouDescription,
        buttonLabel,
        imageUrl,
        imageClassName,
      } = getRedirectUrl(surveyId, rawAnswers);
      setHeaders({
        thankYouHeader,
        thankYouDescription,
        buttonLabel,
        imageUrl,
        imageClassName,
      });
    }

    setStepIndex(prev => prev + 1);
  };

  const handleBack = () => {
    setStepIndex(prev => prev - 1);
  };

  const handleSelect = (value: string) => {
    const choice = step.choices?.find(c => c.value === value);
    if (!choice) return;

    setAnswers(prev => ({ ...prev, [step.label]: value }));
    setRawAnswers(prev => ({ ...prev, [step.stepId]: choice.choiceId }));
  };

  const handleEmail = (value: string) => {
    setEmail(value);
    setAnswers({ ...answers, [step.label]: value });
    setRawAnswers(prev => ({ ...prev, [step.stepId]: value }));
  };

  const handleClose = () => {
    if (email) {
      posthog.alias(email);

      saveBrazeUser({
        email,
      });
    }

    trackEvent({
      event: 'survey_close',
      survey_id: surveyId,
      question_index: stepIndex + 1,
      answers: answers,
      submit_button_url:
        step.submitBehavior === 'dynamic'
          ? redirectUrl
          : step.submitBehavior === 'link'
          ? step.buttonUrl
          : 'close_form',
      email,
      origin: window.location.href,
    });

    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-start bg-lilac-50 animate-fade-in overflow-y-auto">
      <ButtonClose
        onClick={handleClose}
        className=" p-6 md:p-8 self-end"
        size={isLargeScreen ? 24 : 16}
      />

      {step.stepType !== 'submit' ? (
        <div className="max-w-xl w-full h-full flex flex-col items-between mx-auto p-4 md:p-8">
          <Logo className="w-32 mb-8" />
          {step.stepType === 'multiple' && (
            <div className="md:flex-1 h-3/4 md:h-full overflow-y-auto">
              {step.title && (
                <WysiwygContent
                  content={step.title}
                  className="!text-xl font-semibold mb-4"
                />
              )}
              {step.label && (
                <WysiwygContent
                  content={step.label}
                  className="!text-base !text-gray-700 mb-8"
                />
              )}
              <div className="space-y-4">
                {step.choices?.map(({ value }) => (
                  <button
                    key={value}
                    onClick={() => handleSelect(value)}
                    className={`w-full text-center font-semibold text-lg border border-lilac-300 bg-white rounded-lg px-4 py-2 hover:bg-lilac-500 hover:text-white ${
                      answers[step.label] === value &&
                      '!bg-lilac-500 text-white'
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step.stepType === 'email' && (
            <div className="md:flex-1 h-3/4 md:h-full overflow-y-auto">
              {step.title && (
                <WysiwygContent
                  content={step.title}
                  className="!text-xl font-semibold mb-4"
                />
              )}
              {step.label && (
                <WysiwygContent
                  content={step.label}
                  className="!text-base !text-gray-700 mb-4"
                />
              )}
              <input
                type="email"
                value={email}
                onChange={e => handleEmail(e.target.value)}
                className="w-full border border-lilac-300 px-4 py-2 rounded-lg mb-4"
                placeholder="you@example.com"
              />
            </div>
          )}

          <div className="flex justify-between md:mb-8">
            <button
              className="flex items-center gap-x-2 w-fit text-lg font-semibold bg-lilac-300 border-lilac-300 hover:border-lilac-500 hover:bg-lilac-500 hover:text-white py-2 px-4 rounded-lg disabled:opacity-50"
              onClick={handleBack}
              disabled={stepIndex === 0}
            >
              <Image
                src="/arrow.svg"
                width={15}
                height={15}
                alt="back"
                className="rotate-180"
              />
              Back
            </button>
            <button
              className="flex items-center gap-x-2 w-fit text-lg font-semibold bg-lilac-300 border-lilac-300 hover:border-lilac-500 hover:bg-lilac-500 hover:text-white py-2 px-4 rounded-lg disabled:opacity-50"
              onClick={handleNext}
              disabled={
                step.stepType === 'email' ? !email : !answers[step.label]
              }
            >
              Next
              <Image src="/arrow.svg" width={15} height={15} alt="next" />
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-md w-full h-full flex flex-col items-between mx-auto p-4 md:p-8">
          <Logo className="w-32 mb-8" />

          {step.submitBehavior === 'dynamic' ? (
            <div className="md:flex-1 h-3/4 md:h-full overflow-y-auto">
              {headers.thankYouHeader && (
                <WysiwygContent
                  content={headers.thankYouHeader}
                  className="!text-xl font-semibold mb-2"
                />
              )}
              {headers.thankYouDescription && (
                <WysiwygContent
                  content={headers.thankYouDescription}
                  className="!text-base !text-gray-700 mb-6"
                />
              )}
              <Image
                src={headers.imageUrl || ''}
                width={166}
                height={166}
                alt="content and image"
                className={headers.imageClassName || ''}
              />
            </div>
          ) : (
            <div className="md:flex-1 h-3/4 md:h-full overflow-y-auto">
              {step.thankYouHeader && (
                <WysiwygContent
                  content={step.thankYouHeader}
                  className="!text-xl font-semibold mb-2"
                />
              )}
              {step.thankYouDescription && (
                <WysiwygContent
                  content={step.thankYouDescription}
                  className="!text-base !text-gray-700 mb-6"
                />
              )}
            </div>
          )}
          {step.submitBehavior === 'link' ? (
            <a
              href={step.buttonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full text-lg font-semibold bg-lilac-300 border-lilac-300 hover:border-lilac-500 hover:bg-lilac-500 hover:text-white py-2 px-4 rounded-md mt-6 text-center block"
              onClick={() => {
                handleSubmit();
                setShow(false);
              }}
            >
              {step.buttonLabel || 'Get started'}
            </a>
          ) : step.submitBehavior === 'dynamic' ? (
            <button
              className="w-full text-lg font-semibold bg-lilac-300 border-lilac-300 hover:border-lilac-500 hover:bg-lilac-500 hover:text-white py-2 px-4 rounded-md mt-6"
              onClick={() => {
                if (redirectTimeoutRef.current) {
                  clearTimeout(redirectTimeoutRef.current);
                }

                const redirectTo =
                  redirectUrl ||
                  getRedirectUrl(surveyId, rawAnswers)?.buttonUrl;

                if (!hasSubmitted) {
                  handleSubmit();
                }

                redirectNow(redirectTo);
              }}
            >
              {headers.buttonLabel || 'Get started'}
            </button>
          ) : (
            <button
              className="w-full text-lg font-semibold bg-lilac-300 border-lilac-300 hover:border-lilac-500 hover:bg-lilac-500 hover:text-white py-2 px-4 rounded-md mt-6"
              onClick={handleSubmit}
            >
              {step.buttonLabel || 'Submit'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Survey;
